/*
 * WorkAndBuhin.vue の部品変更検知と部品画面を閉じる前の確認判定を切り出した処理。
 */
import { toArray } from "@/components/work-buhin/WorkBuhinAdapter";
import { getBuhinUniqueString } from "@/components/work-buhin/WorkBuhinMatch";
import { INSPECT_TYPE } from "@/model/InspectType";

export interface WorkBuhinChangeOptions {
  isDialog: boolean;
  tmpDeletedManualBuhinList: any[];
}

export type CloseBuhinDialogAction = "close" | "confirm";

export interface CloseBuhinDialogDecisionParams {
  activeWorkIndex: number;
  sknInspectType: any;
  isBuhinNotChange: boolean;
  isKouchinChange: boolean;
  hasPendingBuhinEdit?: boolean;
}

export interface CloseBuhinDialogActionState {
  showConfirmDialog2: boolean;
  shouldClose: boolean;
}

export interface CloseBuhinDialogApplyEffectState {
  shouldSetShowConfirmDialog2: boolean;
  showConfirmDialog2: boolean;
  shouldClose: boolean;
}

export interface BuhinNotChangeState {
  isBuhinNotChange: boolean;
}

export interface OsusumeBuhinQuantityChangeState {
  isQuantityUnchanged: boolean;
}

export interface KouchinChangeState {
  isChange: boolean;
  isKouchinChange: boolean;
}

/*
 * isWorkBuhinNotChanged を WorkAndBuhin.vue から分離し、
 * 変更検知と閉じる判定 を親側置換や単体テストで固定するため。
 */
export function isWorkBuhinNotChanged(
  work: any,
  selectedBuhinList: any[],
  options: WorkBuhinChangeOptions
): boolean {
  let savedBuhinKeys: any[] = [];

  if (work) {
    savedBuhinKeys = toArray(work.buhinList)
      .filter((buhin: any) => buhin.isDelete != 1 && buhin.quantity > 0)
      .map(getBuhinUniqueString);

    const manualBuhinKeys = toArray(work.manualBuhinList)
      .filter((buhin: any) => buhin.isDelete != 1 && buhin.quantity > 0)
      .map(getBuhinUniqueString);
    manualBuhinKeys.forEach((element: any) => {
      if (!savedBuhinKeys.some((buhin: any) => buhin == element)) {
        savedBuhinKeys.push(element);
      }
    });

    if (work.osusumeBuhinList && work.osusumeBuhinList.length > 0) {
      const osusumeBuhinKeys = work.osusumeBuhinList
        .filter(
          (buhin: any) =>
            buhin.isDelete != 1 &&
            (buhin.isSelected || buhin.buhinId) &&
            isOsusumeBuhinQuantityUnchanged(selectedBuhinList, buhin)
        )
        .map(getBuhinUniqueString);
      savedBuhinKeys = [...savedBuhinKeys, ...osusumeBuhinKeys];
    }
  }

  const selectedBuhinKeys = toArray(selectedBuhinList).map(getBuhinUniqueString);

  if (options.isDialog && options.tmpDeletedManualBuhinList.length > 0) {
    savedBuhinKeys = [...savedBuhinKeys, ...options.tmpDeletedManualBuhinList];
  }
  savedBuhinKeys.sort();
  selectedBuhinKeys.sort();

  return JSON.stringify(savedBuhinKeys) == JSON.stringify(selectedBuhinKeys);
}

/*
 * buildBuhinNotChangeState を WorkAndBuhin.vue から分離し、
 * 部品変更なし判定の結果を親側置換や単体テストで固定するため。
 */
export function buildBuhinNotChangeState(
  work: any,
  selectedBuhinList: any[],
  options: WorkBuhinChangeOptions
): BuhinNotChangeState {
  return {
    isBuhinNotChange: isWorkBuhinNotChanged(work, selectedBuhinList, options)
  };
}

/*
 * isOsusumeBuhinQuantityUnchanged を WorkAndBuhin.vue から分離し、
 * 変更検知と閉じる判定 を親側置換や単体テストで固定するため。
 */
export function isOsusumeBuhinQuantityUnchanged(
  selectedBuhinList: any[],
  osusumeBuhin: any
): boolean {
  const isSameBuhin = (buhin: any) =>
    (buhin.buhinOsusumeId && buhin.buhinOsusumeId == osusumeBuhin.buhinOsusumeId) ||
    buhin.buhinCd == osusumeBuhin.buhinCd ||
    buhin.buhinNm == osusumeBuhin.buhinNm;

  return !toArray(selectedBuhinList).some(
    (buhin: any) => isSameBuhin(buhin) && buhin.quantity != osusumeBuhin.quantity
  );
}

/*
 * buildOsusumeBuhinQuantityChangeState を WorkAndBuhin.vue から分離し、
 * おすすめ部品数量変更判定の結果を親側置換や単体テストで固定するため。
 */
export function buildOsusumeBuhinQuantityChangeState(
  selectedBuhinList: any[],
  osusumeBuhin: any
): OsusumeBuhinQuantityChangeState {
  return {
    isQuantityUnchanged: isOsusumeBuhinQuantityUnchanged(
      selectedBuhinList,
      osusumeBuhin
    )
  };
}

/*
 * buildKouchinChangeState を WorkAndBuhin.vue から分離し、
 * 工賃変更時に部品画面全体と工賃変更の dirty state を同時に立てる契約を親側置換や単体テストで固定するため。
 */
export function buildKouchinChangeState(): KouchinChangeState {
  return {
    isChange: true,
    isKouchinChange: true
  };
}

/*
 * resolveCloseBuhinDialogAction を WorkAndBuhin.vue から分離し、
 * 変更検知と閉じる判定 を親側置換や単体テストで固定するため。
 */
export function resolveCloseBuhinDialogAction(
  params: CloseBuhinDialogDecisionParams
): CloseBuhinDialogAction {
  const shouldCheckChange = (
    params.activeWorkIndex !== -1 ||
    params.sknInspectType == INSPECT_TYPE.RECOMMEND ||
    params.sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL
  );
  if (!shouldCheckChange) {
    return "close";
  }
  if (params.isBuhinNotChange && !params.isKouchinChange && !params.hasPendingBuhinEdit) {
    return "close";
  }
  return "confirm";
}

/*
 * buildCloseBuhinDialogActionState を WorkAndBuhin.vue から分離し、
 * 閉じる前確認の判定結果を Vue state 反映と close 実行条件へ変換するため。
 */
export function buildCloseBuhinDialogActionState(
  action: CloseBuhinDialogAction
): CloseBuhinDialogActionState {
  return {
    showConfirmDialog2: action === "confirm",
    shouldClose: action === "close"
  };
}

/*
 * buildCloseBuhinDialogState を WorkAndBuhin.vue から分離し、
 * 閉じる前確認 dialog の判定と state 作成の組み合わせを親側置換や単体テストで固定するため。
 */
export function buildCloseBuhinDialogState(
  params: CloseBuhinDialogDecisionParams
): CloseBuhinDialogActionState {
  return buildCloseBuhinDialogActionState(
    resolveCloseBuhinDialogAction(params)
  );
}

/*
 * buildCloseBuhinDialogApplyEffectState を WorkAndBuhin.vue から分離し、
 * 閉じる前確認 dialog の表示 state 反映と close 実行条件を固定するため。
 */
export function buildCloseBuhinDialogApplyEffectState(
  actionState: CloseBuhinDialogActionState
): CloseBuhinDialogApplyEffectState {
  return {
    shouldSetShowConfirmDialog2: true,
    showConfirmDialog2: actionState.showConfirmDialog2,
    shouldClose: actionState.shouldClose
  };
}
